import { useTheme } from "@mui/material/styles";
import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { FC, useEffect, useMemo, useState } from "react";
import { useTillSummary } from "~/hooks/use-till-summary";
import { $dayjs, DATESTRING_FORMAT, DATEYEARMONTH_FORMAT } from "~/libs/dayjs";
import { GetCurrentCompanyResults } from "~/queries/useGetCurrentCompany";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { GetFixDiscountsResults } from "~/queries/useGetFixDiscounts";
import { GetProductOptionsResults } from "~/queries/useGetProductOptions";
import { GetProductsResults } from "~/queries/useGetProducts";
import { DISCOUNT_TYPE, UpdateFilePDFInput } from "~/types/__generated/gql/graphql";
import { bigMath, formatNumber } from "~served/utils";
import { initPdfState } from "./invoice-pdf-dialog";
const useStyles = () => {
  const theme = useTheme();
  return useMemo(() => {
    return StyleSheet.create({
      page: {
        fontFamily: "Noto Serif SC",
        backgroundColor: "#FFFFFF",
        padding: 24
      },
      h4: {
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.235
      },
      h6: {
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.6
      },
      alignRight: {
        textAlign: "right",
        float: "right"
      },
      subtitle2: {
        fontSize: 8.5,
        fontWeight: 800,
        lineHeight: 1.57,
        wordBreak: "break-all"
      },
      body2: {
        fontSize: 8.5,
        fontWeight: 400,
        lineHeight: 1.43
      },
      gutterBottom: {
        marginBottom: 4
      },
      graySection: {
        width: "50vw",
        backgroundColor: "#e6e6e6",
        padding: 5
      },
      colorSuccess: {
        color: theme.palette.success.main
      },
      colorError: {
        color: theme.palette.error.main
      },
      uppercase: {
        textTransform: "uppercase"
      },
      header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
      },
      contentLeft: {
        width: "50%"
      },
      contentRight: {
        width: "25%"
      },
      brand: {
        height: 24,
        width: 24
      },
      company: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 8
      },
      billing: {
        marginTop: 8
      },
      items: {
        marginTop: 16
      },
      itemRow: {
        borderBottomWidth: 1,
        borderColor: "#EEEEEE",
        borderStyle: "solid",
        flexDirection: "row"
      },
      itemNumber: {
        padding: 6,
        width: "10%"
      },
      itemDescription: {
        padding: 6,
        width: "50%"
      },
      itemQty: {
        padding: 6,
        width: "10%"
      },
      itemUnitAmount: {
        padding: 6,
        width: "15%"
      },
      itemTotalAmount: {
        padding: 6,
        width: "15%"
      },
      summaryRow: {
        flexDirection: "row"
      },
      summaryGap: {
        padding: 6,
        width: "70%"
      },
      summaryTitle: {
        padding: 6,
        width: "35%"
      },
      summaryValue: {
        padding: 6,
        width: "15%"
      },
      payments: {
        marginTop: 36,
        borderBottomWidth: 1,
        borderColor: "#d9d9d9",
        borderStyle: "solid"
      },
      paymentRow: {
        flexDirection: "row"
      },
      paymentDate: {
        border: "1px",
        borderBottom: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "10%"
      },
      bankReceivedDate: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "20%"
      },
      paymentType: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "50%"
      },
      paymentAmount: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "20%"
      },
      paymentSummaryLabel: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "80%",
        textAlign: "right"
      },
      paymentSummaryValue: {
        border: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        padding: 6,
        width: "20%"
      },
      notes: {
        marginTop: 32
      }
    });
  }, [theme]);
};
export const InvoicePdfDocument: FC<{
  file: GetFileByIdResults;
  fixDiscount: number;
  fixDiscounts: GetFixDiscountsResults;
  company: GetCurrentCompanyResults;
  itemsSummary: ReturnType<typeof useTillSummary>["updateFileInputSummary"];
  products: GetProductsResults;
  productOptions: GetProductOptionsResults;
  isAllApplyFixeDiscount: boolean;
  pdfState: UpdateFilePDFInput;
}> = ({
  file,
  fixDiscount,
  fixDiscounts,
  company,
  itemsSummary,
  products,
  productOptions,
  isAllApplyFixeDiscount,
  pdfState
}) => {
  const styles = useStyles();
  const [state, setState] = useState<UpdateFilePDFInput>(initPdfState);
  useEffect(() => {
    if (pdfState) setState(pdfState);
  }, [pdfState]);

  // const isAllApplyFixeDiscount = useMemo(() => {
  // 	if (file) {
  // 		for (const product of file.products) {
  // 			if (
  // 				!product.subtotalAddons.fixDiscount.fixDiscount ||
  // 				product.subtotalAddons.fixDiscount.amount === 0
  // 			) {
  // 				return false;
  // 			}
  // 		}
  // 	}
  // 	return true;
  // }, [file]);

  return <Document data-sentry-element="Document" data-sentry-component="InvoicePdfDocument" data-sentry-source-file="invoice-pdf-document.tsx">
			<Page size="A4" style={styles.page} data-sentry-element="Page" data-sentry-source-file="invoice-pdf-document.tsx">
				<View style={styles.header} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<View style={styles.contentLeft} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<Image
          // src={company.logo!}
          src="/favicon.png" style={styles.brand} data-sentry-element="Image" data-sentry-source-file="invoice-pdf-document.tsx" />
					</View>
					<View style={styles.contentRight} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						{file._receipt?.isFullyPaid ? <Text style={[styles.h4, styles.uppercase, styles.colorSuccess]}>
								PAID
							</Text> : <Text style={[styles.h4, styles.uppercase, styles.colorError]}>
								UNPAID
							</Text>}
					</View>
				</View>
				<View style={styles.header} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<View style={styles.contentLeft} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">{company.name}</Text>
					</View>
					<View style={styles.contentRight} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						{file?.counterConfigs?.invoice ? <Text style={[styles.subtitle2]}>
								{file._receipt?.isFullyPaid ? "Receipt #: " : "Invoice #: "}
								{file.counterConfigs?.invoice?._counter.isPredefinedFormat ? `${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.invoice?._counter.prefix}
								{"-"}
								{file.counterConfigs.invoice?.index ? String(file.counterConfigs.invoice?.index || 0).padStart(file.counterConfigs.invoice?._counter?.suffix_digit || 0, "0") : ""}
								{"-"}
								{file.receiptSuffixComment}
							</Text> : <Text style={[styles.subtitle2]}>INV-XXXXXX</Text>}
					</View>
				</View>
				<View style={styles.company} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<View style={styles.contentLeft} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<Text style={[styles.body2, {
            width: "150px"
          }]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
							{company.address}
						</Text>
					</View>
					<View style={styles.contentRight} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<Text style={[styles.subtitle2, styles.gutterBottom]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
							Date of issue: {$dayjs(file.issueDate).format(DATESTRING_FORMAT)}
						</Text>
						<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
							Due date:{" "}
							{file.dueDate ? $dayjs(file.dueDate).format(DATESTRING_FORMAT) : ""}
						</Text>
					</View>
				</View>
				<View style={styles.billing} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<Text style={[styles.subtitle2, styles.gutterBottom]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						Billed to: {file._client?.name ?? ""}
					</Text>
				</View>
				<View style={styles.graySection} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						Attention:{" "}
						{file._client?.contacts && file._client.contacts.length > 0 ? file._client.contacts[0].firstName : ""}
						&nbsp;
						{file._client?.contacts && file._client.contacts.length > 0 ? file._client.contacts[0].lastName : ""}
					</Text>
					<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						Telephone:{" "}
						{file._client?.contacts && file._client.contacts.length > 0 ? file._client.contacts[0].telephone : ""}
					</Text>
					<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						Email:{" "}
						{file._client?.contacts && file._client.contacts.length > 0 ? file._client.contacts[0].email : ""}
					</Text>
					<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						Address:{" "}
						{file._client?.addresses && file._client.addresses.length > 0 ? [file._client.addresses[0].building, file._client.addresses[0].street, file._client.addresses[0].area, file._client.addresses[0].city, file._client.addresses[0].country].join(", ") : ""}
					</Text>
				</View>
				<View style={styles.items} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<View style={styles.itemRow} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<View style={styles.itemNumber} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">#</Text>
						</View>
						<View style={styles.itemDescription} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Product</Text>
						</View>
						<View style={styles.itemQty} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Qty</Text>
						</View>
						<View style={styles.itemUnitAmount} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.h6} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Unit Price</Text>
						</View>
						<View style={styles.itemTotalAmount} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={[styles.h6, styles.alignRight]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Total</Text>
						</View>
					</View>
					{itemsSummary.products.map((item, index) => {
          return <View key={item._id.toString()} style={styles.itemRow}>
								<View style={styles.itemNumber}>
									<Text style={styles.body2}>{index + 1}</Text>
								</View>
								<View style={styles.itemDescription}>
									<Text style={styles.body2}>
										{products.find(p => p._id === item.product)?.name ?? "[UNKNOWN PRODUCT]"}
									</Text>
									<Text style={styles.body2}>
										{item.options.map(option => productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]").join(", ")}
									</Text>
									{item.subtotalAddons && !isAllApplyFixeDiscount && <>
											{item.subtotalAddons.fixDiscount.amount > 0 && <Text style={styles.body2}>
													{item.subtotalAddons.fixDiscount.fixDiscount ? fixDiscounts.find(d => d._id === item.subtotalAddons.fixDiscount.fixDiscount)?.name : `${item.subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.fixDiscount.amount}" discount` : `"${item.subtotalAddons.fixDiscount.value} %" discount`}`}
												</Text>}
										</>}
								</View>
								<View style={styles.itemQty}>
									<Text style={styles.body2}>{item.quantity}</Text>
								</View>
								<View style={styles.itemUnitAmount}>
									<Text style={[styles.body2, styles.alignRight]}>
										${formatNumber(bigMath.div(item.subtotal, item.quantity))}
									</Text>
								</View>
								<View style={styles.itemTotalAmount}>
									<Text style={[styles.body2, styles.alignRight]}>
										${formatNumber(item.subtotal)}
									</Text>
									{item.subtotalAddons && !isAllApplyFixeDiscount && <>
											{item.subtotalAddons.fixDiscount.amount > 0 && <Text style={[styles.body2, styles.alignRight]}>
													- $
													{formatNumber(item.subtotalAddons.fixDiscount.amount)}
												</Text>}
										</>}
								</View>
							</View>;
        })}
					<View style={styles.summaryRow} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<View style={styles.summaryGap} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx" />
						<View style={[styles.summaryTitle, styles.alignRight]} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Subtotal</Text>
						</View>
						<View style={styles.summaryValue} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={[styles.body2, styles.alignRight]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
								${formatNumber(itemsSummary.subtotal)}
							</Text>
						</View>
					</View>
					<View style={styles.summaryRow} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<View style={styles.summaryGap} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx" />
						<View style={[styles.summaryTitle, styles.alignRight]} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
								Fix Discount
								{isAllApplyFixeDiscount ? `: "${itemsSummary.products[0]?.subtotalAddons.fixDiscount?.fixDiscount ? fixDiscounts.find(d => d._id === itemsSummary.products[0].subtotalAddons.fixDiscount.fixDiscount)?.name : itemsSummary.products[0].subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `$ ${itemsSummary.products[0].subtotalAddons.fixDiscount.value} discount` : `${itemsSummary.products[0].subtotalAddons.fixDiscount.value} % discount`}"` : ""}
							</Text>
						</View>
						<View style={styles.summaryValue} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={[styles.body2, styles.alignRight]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
								- ${formatNumber(fixDiscount)}
							</Text>
						</View>
					</View>
					<View style={styles.summaryRow} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<View style={styles.summaryGap} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx" />
						<View style={[styles.summaryTitle, styles.alignRight]} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Taxes</Text>
						</View>
						<View style={styles.summaryValue} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={[styles.body2, styles.alignRight]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">$ 0</Text>
						</View>
					</View>
					<View style={styles.summaryRow} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
						<View style={styles.summaryGap} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx" />
						<View style={[styles.summaryTitle, styles.alignRight]} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Total</Text>
						</View>
						<View style={styles.summaryValue} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
							<Text style={[styles.body2, styles.alignRight]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
								${formatNumber(itemsSummary.grossAmount)}
							</Text>
						</View>
					</View>
				</View>
				{file._receipt && <View style={styles.payments}>
						<View style={styles.paymentRow}>
							<View style={styles.paymentDate}>
								<Text style={styles.h6}>Date</Text>
							</View>
							<View style={styles.bankReceivedDate}>
								<Text style={styles.h6}>Bank Received</Text>
							</View>
							<View style={styles.paymentType}>
								<Text style={styles.h6}>Payment Type</Text>
							</View>
							<View style={styles.paymentAmount}>
								<Text style={styles.h6}>Payment Amount</Text>
							</View>
						</View>
						{file._receipt.payments.map((payment, i) => {
          return <View key={`payment-${i}`} style={styles.paymentRow}>
									<View style={styles.paymentDate}>
										<Text style={styles.body2}>
											{$dayjs(payment.paidAt).format(DATESTRING_FORMAT)}
										</Text>
									</View>
									<View style={styles.bankReceivedDate}>
										{payment.payments.map((p, i) => <Text key={`payment-type-${p.payment_type}-${i}`} style={styles.body2}>
												{p.bank_received_date ? $dayjs(p.bank_received_date).format(DATESTRING_FORMAT) : ""}
											</Text>)}
									</View>
									<View style={styles.paymentType}>
										{payment.payments.map((p, i) => <Text key={`payment-type-${p.payment_type}-${i}`} style={styles.body2}>
												{company.paymentTypes.find(pt => pt._id.toString() === p.payment_type.toString())?.name ?? "[UNKNOWN TYPE]"}
											</Text>)}
									</View>
									<View style={styles.paymentAmount}>
										{payment.payments.map((p, i) => <Text key={`payment-amt-${p.amount}-${i}`} style={styles.body2}>
												{formatNumber(p.amount)}
											</Text>)}
									</View>
								</View>;
        })}

						<View key={`total-payment-received-amount-label`} style={styles.paymentRow}>
							<View style={styles.paymentSummaryLabel}>
								<Text style={styles.body2}>Total Payment Received Amount</Text>
							</View>
							<View style={styles.paymentSummaryValue}>
								<Text key={`total-payment-received-amount`} style={styles.body2}>
									{formatNumber(file._receipt.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0)}
								</Text>
							</View>
						</View>

						<View key={`total-payment-received-amount-label`} style={styles.paymentRow}>
							<View style={styles.paymentSummaryLabel}>
								<Text style={styles.body2}>Outstanding Balance Amount</Text>
							</View>
							<View style={styles.paymentSummaryValue}>
								<Text key={`total-payment-received-amount`} style={styles.body2}>
									{formatNumber(bigMath.sub(itemsSummary.grossAmount, file._receipt.payments ? file._receipt.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) : 0))}
								</Text>
							</View>
						</View>
					</View>}
				<View style={styles.notes} data-sentry-element="View" data-sentry-source-file="invoice-pdf-document.tsx">
					<Text style={[styles.h6, styles.gutterBottom]} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">Notes</Text>
					<Text style={styles.body2} data-sentry-element="Text" data-sentry-source-file="invoice-pdf-document.tsx">
						{file._receipt?.isFullyPaid ? state.receiptNote && state.receiptNote !== "" ? state.receiptNote : company.receiptComment ? company.receiptComment : "Please make sure you have the right bank registration number as I had issues before and make sure you guys cover transfer expenses." : state.receiptNote && state.receiptNote !== "" ? state.receiptNote : company.invoiceComment ? company.invoiceComment : "Please make sure you have the right bank registration number as I had issues before and make sure you guys cover transfer expenses."}
					</Text>
				</View>
			</Page>
		</Document>;
};